import { useContext, useEffect, useRef } from 'react';
import { Textarea, Text, Button, useMantineTheme, Badge, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { ChatContext } from '../context/ChatContext';
import ReactMarkdown from "react-markdown";
import ConfigModel from './ConfigModel';
import ResetChat from './ResetChat';

const Chat = () => {
  const { messages, sendMessage, loading, handleShowVariables, promtRef, modelConfig } = useContext(ChatContext)
  const isEmptyMessage = !messages?.length
  const form = useForm({
    initialValues: { prompt: '' },
    validate: { prompt: (value) => !!value ? null : 'Type text to chat with AI' },
  });
  const theme = useMantineTheme();
  const isTet = theme.colorScheme === 'tet';
  const handleKeyDown = (event) => {
    event.stopPropagation()
    if (event.shiftKey && event.key === "Enter") {
      form.setFieldValue('prompt', form.getInputProps('prompt').value + '\n')
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      form.onSubmit(onSubmitForm)();
    }
    promtRef?.current?.focus()
  };

  const onSubmitForm = (values) => {
    form.setFieldValue('prompt', '');
    sendMessage(values.prompt);
  }

  useEffect(() => {
    promtRef?.current?.focus()
  }, [messages])

  const getModelInfo = () => {
    if (!modelConfig?.model) return 'Choose a model to start chatting';
    return `${modelConfig.provider} (${modelConfig.model || '—'})`;
  };

  return (
    <div
      style={{
        minHeight: isEmptyMessage ? 0 : '100%',
        height: isEmptyMessage ? undefined : '100%',
        position: 'relative',
        padding: 8,
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 4,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingLeft: 8,
          paddingRight: 8,
          boxSizing: 'border-box',
          pointerEvents: 'none',
        }}
      >
        <div style={{ display: 'flex', marginBottom: 4, pointerEvents: 'auto', flexWrap: 'wrap', justifyContent: 'center' }}>
          <ResetChat />
          <Button
            variant="gradient"
            size="xs"
            onClick={handleShowVariables}
            mr={8}
            gradient={
              isTet
                ? { from: '#d32f2f', to: '#fbc02d', deg: 90 }
                : { from: 'indigo', to: 'cyan', deg: 90 }
            }
          >
            Variables
          </Button>
          {modelConfig?.model && (
            <ConfigModel buttonSize="xs" buttonLabel="Config Model" />
          )}
        </div>
      </div>

      {!modelConfig?.model ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            marginTop: 44,
          }}
        >
          <Stack align="center" spacing="lg" maw={360} style={{ textAlign: 'center' }}>
            <Text size="md" weight={600}>
              Choose a model to start chatting
            </Text>
            <Text size="sm" color="dimmed">
              You need to configure the provider (OpenAI, Anthropic, Gemini), model and API key. The Config Model button will appear after saving.
            </Text>
            <ConfigModel
              buttonSize="sm"
              buttonLabel="Config Model"
            />
          </Stack>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            fontSize: '0.85rem',
            marginTop: 45,
          }}
        >
          {
            messages.map((mesage, idx) => mesage.type === 'model' ? (
              <p
                style={{
                  padding: 6,
                  marginTop: 12,
                }}
                key={idx}
              >
                <ReactMarkdown>{mesage.message}</ReactMarkdown>
              </p>
            ) : (
              <p
                style={{
                  padding: 6,
                  borderRadius: 12,
                  marginTop: 12,
                  textAlign: 'right',
                  backgroundColor: '#006fe659',
                }}
                key={idx}
              >
                {mesage.message}
              </p>
            ))
          }
          {
            loading && (
              <Text
                fz="sm"
                mt={12}
                align="center"
                italic
                style={{ marginBottom: 8 }}
              >
                AI is typing...
              </Text>
            )
          }
        </div>
      )}

      {modelConfig?.model && (
        <form
          style={{
            width: '100%',
            flexShrink: 0,
            marginTop: 'auto',
          }}
          onSubmit={form.onSubmit(onSubmitForm)}
        >
          <Textarea
            disabled={loading}
            autosize
            minRows={3}
            maxRows={10}
            style={{ width: '100%' }}
            onKeyDown={handleKeyDown}
            placeholder="What can I help with?"
            spellCheck={false}
            ref={promtRef}
            styles={{
              input: { paddingBottom: '25px !important' },
              overflow: "auto"
            }}
            inputContainer={(children) => (
              <div style={{ position: 'relative' }}>
                {children}
                <Badge
                  size="sm"
                  variant="light"
                  color="blue"
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 12,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex: 1,
                    paddingLeft: 10,
                    paddingRight: 10,
                    border: '1px solid var(--mantine-color-blue-3)',
                    borderRadius: 4,
                  }}
                >
                  {getModelInfo()}
                </Badge>
              </div>
            )}
            {...form.getInputProps('prompt')}
          />
        </form>
      )}
    </div>
  )
}

export default Chat
